/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ProgressIndicator } from '@fluentui/react';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { TruncatedText } from 'src/core/components/TruncatedText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { EvaluationJobStore } from 'src/modules/modelEvaluation/EvaluationJobInfoStore';

const messages = defineMessages({
    evaluation: { id: 'evaluationJobTable.evaluationProgressIndicator.evaluation', defaultMessage: 'Evaluation' },
    complete: { id: 'evaluationJobTable.evaluationProgressIndicator.complete', defaultMessage: 'complete' },
    evaluationComplete: {
        id: 'evaluationJobTable.evaluationProgressIndicator.evaluationComplete',
        defaultMessage: 'Evaluation completed.'
    },
    jobNotStartedDescription: {
        id: 'evaluationJobTable.evaluationProgressIndicator.jobNotStartedDescription',
        defaultMessage: 'Job in queue. Starting soon ... '
    }
});

type EvaluationProgressIndicatorProps = { maxWidth?: number; evaluationJob: EvaluationJobStore };

export const EvaluationProgressIndicator = observer((props: EvaluationProgressIndicatorProps) => {
    const { evaluationJob, maxWidth } = props;
    const { localize } = useLocalization();

    let description: string;

    if (evaluationJob?.status === 'notStarted') {
        description = localize(messages.jobNotStartedDescription);
    } else {
        description = `${localize(messages.evaluation)} - ${evaluationJob?.percentComplete ?? '--'}% ${localize(messages.complete)}.`;
    }

    return (
        <ProgressIndicator
            styles={{ root: { flex: 1 } }}
            percentComplete={evaluationJob.percentComplete / 100}
            description={
                <TruncatedText block variant="small" maxWidth={maxWidth}>
                    {description}
                </TruncatedText>
            }
        />
    );
});
